import React from 'react';
import { X } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { formatPrice } from '../utils/priceFormatter';

const CartModal = ({ isOpen, onClose, cartItem }) => {
  const navigate = useNavigate();

  if (!isOpen || !cartItem) return null;

  const calculateTotal = () => {
    return cartItem.totalPrice;
  };

  const shippingCost = calculateTotal() >= 150 ? 0 : 19.20;
  const totalTTC = calculateTotal() + shippingCost;

  return (
    <>
      {/* Overlay */}
      <div 
        className="fixed inset-0 bg-black bg-opacity-50 z-40"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 flex items-center justify-center z-50 p-4">
        <div className="bg-white rounded-lg shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
          {/* Success Banner */}
          <div className="bg-celadon text-white p-4 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <span className="text-2xl">✓</span>
              <span className="font-medium">Produit ajouté avec succès à votre panier</span>
            </div>
            <button 
              onClick={onClose}
              className="text-white hover:text-gray-200 transition-colors"
            >
              <X size={24} />
            </button>
          </div>

          {/* Content */}
          <div className="p-8">
            <div className="grid grid-cols-2 gap-8">
              {/* Left - Product Info */}
              <div className="flex gap-4">
                <img 
                  src={cartItem.product.image} 
                  alt={cartItem.product.name}
                  className="w-32 h-32 object-cover rounded border"
                />
                <div className="flex-1">
                  <h3 className="font-medium text-gray-900 mb-2">
                    {cartItem.product.name}
                  </h3>
                  <p className="text-2xl font-bold text-red-600 mb-3">
                    {formatPrice(cartItem.totalPrice)} €
                  </p>
                  <p className="text-gray-600">
                    Quantité: {cartItem.configuration.quantity}
                  </p>
                </div>
              </div>

              {/* Right - Cart Summary */}
              <div className="border-l pl-8">
                <h3 className="text-xl font-medium mb-4">
                  Il y a {cartItem.configuration.quantity} article{cartItem.configuration.quantity > 1 ? 's' : ''} dans votre panier.
                </h3>
                
                <div className="space-y-3 mb-6">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Total:</span>
                    <span className="font-medium">{formatPrice(calculateTotal())} €</span>
                  </div>
                  
                  <div className="flex justify-between">
                    <span className="text-gray-600">Livraison:</span>
                    <span className="font-medium">
                      {shippingCost === 0 ? 'Offerte' : `${formatPrice(shippingCost)} €`}
                    </span>
                  </div>
                  
                  <div className="pt-3 border-t">
                    <div className="flex justify-between text-lg">
                      <span className="font-bold">Total TTC</span>
                      <span className="font-bold">{formatPrice(totalTTC)} €</span>
                    </div>
                  </div>
                </div>

                {/* Action Buttons */}
                <div className="flex gap-3">
                  <button
                    onClick={onClose}
                    className="flex-1 py-3 bg-celadon text-white rounded font-medium hover:bg-celadon-light transition-colors"
                  >
                    Continuer vos achats
                  </button>
                  <button
                    onClick={() => {
                      onClose();
                      // Force a full page navigation to ensure proper route change
                      window.location.href = '/commande';
                    }}
                    className="flex-1 py-3 bg-celadon text-white rounded font-medium hover:bg-celadon-light transition-colors flex items-center justify-center gap-2"
                  >
                    <span>✓</span>
                    Valider ma commande
                  </button>
                </div>
              </div>
            </div>

            {/* Configuration Details */}
            <div className="mt-6 pt-6 border-t">
              <h4 className="font-medium text-gray-900 mb-3">Détails de la configuration :</h4>
              <div className="grid grid-cols-2 gap-4 text-sm text-gray-600">
                <div>
                  <span className="font-medium">Dimensions:</span> {cartItem.configuration.largeur} x {cartItem.configuration.projection} cm 
                  ({((cartItem.configuration.largeur * cartItem.configuration.projection) / 10000).toFixed(2)} m²)
                </div>
                <div>
                  <span className="font-medium">Fixation haute:</span> {cartItem.configuration.fixationHaute}
                </div>
                <div>
                  <span className="font-medium">Fixation basse:</span> {cartItem.configuration.fixationBasse}
                </div>
                <div>
                  <span className="font-medium">Lambrequin:</span> {cartItem.configuration.lambrequin}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default CartModal;